import PropTypes from 'prop-types';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import { FeatureDecisionContext } from '../../shared/contexts/FeatureDecisionContext';
import mode from '../../shared/helpers/Mode';
import {
  MEETING_METHODS,
  SHORTCUTS,
  STEPS,
  USER_PREFERENCE,
} from '../constants';
import Api from '../helpers/Api';
import {
  filterMethods,
  shouldSkipLocationStep,
} from '../helpers/MeetingMethods';
import Resources from '../helpers/Resources';
import Shortcuts from '../helpers/Shortcuts';
import { URLtoCleanString } from '../helpers/Url';
import CheckIn from '../steps/CheckIn';
import DateTime from '../steps/DateTime';
import Details from '../steps/Details';
import Location from '../steps/Location';
import MeetingMethods from '../steps/MeetingMethods';
import Service from '../steps/Service';
import { StaffPreference } from '../steps/StaffPreference';
import { LocaleContext } from './LocaleContext';
import { SelectionContext } from './SelectionContext';
import { SettingsContext } from './SettingsContext';
import { TimezoneContext } from './TimezoneContext';

let hasSetTimezone = false;

const initialAppointmentSteps = [
  {
    id: STEPS.SERVICE,
    component: Service,
  },
  {
    id: STEPS.LOCATION,
    component: Location,
  },
  {
    id: STEPS.MEETING_METHODS,
    component: MeetingMethods,
  },
  {
    id: STEPS.TIMES,
    component: DateTime,
  },
  {
    id: STEPS.DETAILS,
    component: Details,
  },
  {
    id: STEPS.CHECK_IN,
    component: CheckIn,
  },
];

const initialWalkinSteps = [
  {
    id: STEPS.SERVICE,
    component: Service,
  },
  {
    id: STEPS.STAFF_PREFERENCE,
    component: StaffPreference,
  },
  {
    id: STEPS.DETAILS,
    component: Details,
  },
];

const StepContext = createContext([]);

const StepProvider = ({ children }) => {
  const history = useHistory();
  const [locale] = useContext(LocaleContext);
  const { firstStep, includeLocationStep } = useContext(SettingsContext);
  const [{ location, service, user, userPreference }, setSelections] =
    useContext(SelectionContext);
  const [, setTimezone] = useContext(TimezoneContext);
  const { kioskSelectPreferredLang, kioskSelectPreferredStaff } =
    useContext(SettingsContext);

  const { shouldUseKioskEnhancements, shouldUseKioskEnhancementsRelease } =
    useContext(FeatureDecisionContext);
  const useNewKiosk = shouldUseKioskEnhancementsRelease || mode.useNewKiosk();
  const isWalkIn = mode.isWalkIn();

  const initialSteps = useMemo(() => {
    if (shouldUseKioskEnhancements && useNewKiosk && isWalkIn) {
      if (!kioskSelectPreferredLang && !kioskSelectPreferredStaff) {
        return initialWalkinSteps.filter(
          (step) => step.id !== STEPS.STAFF_PREFERENCE,
        );
      }

      return initialWalkinSteps;
    }

    return initialAppointmentSteps;
  }, [
    shouldUseKioskEnhancements,
    useNewKiosk,
    isWalkIn,
    kioskSelectPreferredLang,
    kioskSelectPreferredStaff,
  ]);

  const order = window.state.steps;
  const sorter = (a, b) => order.indexOf(a.id) - order.indexOf(b.id);
  const filterer = (step) => order.findIndex((item) => item === step.id) >= 0;
  const mapper = (step) => {
    step.locked = false;

    if (step.id === STEPS.LOCATION && location && location.locked) {
      step.locked = true;
    }

    if (step.id === STEPS.LOCATION && Shortcuts.exists(SHORTCUTS.LOCATION)) {
      step.locked = true;

      if (
        !hasSetTimezone &&
        location &&
        location.timezone &&
        location.physical
      ) {
        setTimezone(location.timezone);
        hasSetTimezone = true;
      }
    }

    if (step.id === STEPS.MEETING_METHODS) {
      if (
        !hasSetTimezone &&
        location &&
        location.timezone &&
        location.physical
      ) {
        setTimezone(location.timezone);
        hasSetTimezone = true;
      }

      let method = Shortcuts.get(SHORTCUTS.MEETING_METHOD);

      if (!method) {
        const methods = filterMethods(service, user);

        method =
          methods.length === 1 && methods[0].id === MEETING_METHODS.AT_LOCATION
            ? MEETING_METHODS.AT_LOCATION
            : method;
      }

      if (method) {
        const skipLocation = shouldSkipLocationStep(
          method,
          service,
          firstStep,
          includeLocationStep,
        );

        step.locked = skipLocation || Shortcuts.exists(SHORTCUTS.LOCATION);
      } else {
        step.locked = false;
      }
    }

    if (
      step.id === STEPS.SERVICE &&
      Shortcuts.exists(SHORTCUTS.SERVICE) &&
      !(shouldUseKioskEnhancements && useNewKiosk && isWalkIn)
    ) {
      step.locked = true;
    }

    if (step.id === STEPS.TIMES && Shortcuts.exists(SHORTCUTS.TIMESLOT)) {
      step.locked = true;
    }

    return step;
  };
  const locker = (a, b) =>
    a.locked === b.locked ? 0 : a.locked === true ? -1 : 1;

  const [steps, setSteps] = useState(() =>
    initialSteps.filter(filterer).sort(sorter).map(mapper).sort(locker),
  );

  const lockStep = (id) => {
    setSteps(
      steps.map((step) => (step.id === id ? { ...step, locked: true } : step)),
    );
  };
  const remapSteps = () => {
    setSteps(steps.map(mapper).sort(locker));
  };

  const resetInitialSteps = () => {
    setSteps(
      initialSteps.filter(filterer).sort(sorter).map(mapper).sort(locker),
    );
  };

  useEffect(() => {
    if (isWalkIn) {
      setSelections({
        bookingWalkIn: true,
        meetingMethod: MEETING_METHODS.AT_LOCATION,
      });
    }
  }, [isWalkIn, setSelections]);

  useEffect(() => {
    if (shouldUseKioskEnhancements && useNewKiosk && isWalkIn) {
      setSteps(initialSteps);
    }
    // not all dependencies are memoized
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldUseKioskEnhancements, useNewKiosk, isWalkIn]);

  // Update url with user preference
  useEffect(() => {
    const searchParams = new URLSearchParams(history.location.search);
    const originalSearchStr = searchParams.toString();

    const shouldAddUrlParams =
      mode.isWalkIn() && useNewKiosk && shouldUseKioskEnhancements;

    if (!shouldAddUrlParams) {
      return;
    }

    if (userPreference?.id !== searchParams.get('preferred_lang')) {
      if (
        userPreference?.id &&
        userPreference?.id !== USER_PREFERENCE.SPECIFIC &&
        userPreference?.id !== USER_PREFERENCE.RANDOM
      ) {
        searchParams.set('preferred_lang', userPreference?.id);
      } else {
        searchParams.delete('preferred_lang');
      }
    }

    if (user?.id && userPreference?.id === USER_PREFERENCE.SPECIFIC) {
      searchParams.set('preferred_staff_id', user?.id);
    } else if (
      searchParams.has('preferred_staff_id') &&
      user === null &&
      (userPreference?.id === USER_PREFERENCE.SPECIFIC ||
        userPreference?.id === USER_PREFERENCE.RANDOM)
    ) {
      searchParams.delete('preferred_staff_id');
    }

    if (searchParams.toString() !== originalSearchStr) {
      setSelections({ search: URLtoCleanString(searchParams) });
      history.push({ search: URLtoCleanString(searchParams) });
    }
  }, [
    user,
    userPreference?.id,
    shouldUseKioskEnhancements,
    history,
    setSelections,
    useNewKiosk,
    locale,
  ]);

  useEffect(() => {
    const searchParams = new URLSearchParams(history.location.search);
    const shouldAddUrlParams =
      mode.isWalkIn() && useNewKiosk && shouldUseKioskEnhancements;

    if (!shouldAddUrlParams) {
      return;
    }

    if (
      searchParams.has('preferred_staff_id') &&
      userPreference?.id !== null &&
      user === null
    ) {
      Api.locale(locale)
        .users()
        .find({ user: searchParams.get('preferred_staff_id') })
        .then(({ data }) => {
          setSelections({
            user: Resources.formatUser(data),
            userPreference: { id: USER_PREFERENCE.SPECIFIC },
          });
        });
    }
  }, []);

  return (
    <StepContext.Provider
      value={[steps, lockStep, remapSteps, resetInitialSteps]}
    >
      {children}
    </StepContext.Provider>
  );
};

StepProvider.propTypes = {
  children: PropTypes.element.isRequired,
};

export { StepContext, StepProvider };
